import { API } from '@aws-amplify/api'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import getHomesPage from '../../../services/contentful/homes'
import getGlobalFlags from '../../../services/flags/getGlobalFlags'

import {
  formatFooterFields,
  formatNavFields,
} from '../../../utils/contentful/format-global-fields'

import { useMarketParam } from '../../../hooks/use-market-param'
import { useListingQueryItem } from '../../../hooks/use-listing-query-item'
import { useListings } from '../../../services/getListings'
import { useListingDetails } from '../../../services/getListingById'

import { HomesInMarket } from '../../../components/homes/homes-in-market'
import { ListingsProvider } from '../../../services/context/ListingsContext'

import type { Listing } from '../../../services/getListings'
import type { CalculatorPageContent } from '../../../hooks/useAcreCalculator'

import type { IBasicPageProps } from '../../[slug]'

import type { GetStaticPaths, GetStaticProps } from 'next'
import getFooterGuides from '../../../services/website/getFooterGuides'

type HomesInMarketPageProps = IBasicPageProps & {
  initial: { market: string; listings: Listing[] }
  seoImage?: string
  calculator: CalculatorPageContent
}

export default function HomesInMarketPage(props: HomesInMarketPageProps) {
  const market = useMarketParam()
  const listingId = useListingQueryItem()

  const getListingsResult = useListings(
    market,
    props.initial.market === market ? props.initial.listings : undefined
  )

  const getListingResult = useListingDetails(
    listingId ?? 0,
    undefined,
    listingId === undefined
  )

  return (
    <ListingsProvider>
      <HomesInMarket
        {...props}
        market={market}
        listings={getListingsResult.data ?? []}
        fetchingListings={getListingsResult.isFetching}
        listing={getListingResult.data}
      />
    </ListingsProvider>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStaticProps: GetStaticProps<
  any,
  { state: string; market: string }
> = async (context) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const market = `${context.params!.state}/${context.params!.market}`
  const listings = await API.get('listing', '/search', {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryStringParameters: { market },
  }).catch(() => [])

  const content = await getHomesPage()
  const globalFlags = await getGlobalFlags()

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', [
        'common',
        'homes',
        'calculator',
      ])),
      initial: { market, listings },
      calculator: content.pageCalculatorCollection.items[0] ?? {},
      nav: formatNavFields(content),
      footer: {
        ...formatFooterFields(content),
        footerGuidesLinks: await getFooterGuides(),
      },
      siteBanner: content?.globals.siteBanner,
      marketingBanner: content?.globals.marketingBanner,
      seoImage: content?.globals.shareImageHomes?.url ?? null,
      ...globalFlags,
    },
    revalidate: 120,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}
